import { realtime } from '../api/realtime.js';
import { messageFor } from '../api/errors.js';
import { showToast } from './Toast.js';
import { createCarSprite, fitCarHitArea, setCarFacing, setCarDepth } from './CarSprite.js';
import { ROAD_WHEEL_Y, carGroundYFromCentre } from './carCatalog.mjs';
import { isSchoolCarId } from './schoolCar.mjs';

// Cars are world state that outlives their owner's socket: one parked on the street is in
// everyone's roster whether or not its owner is online. attachRemotePresence already owns
// the room subscription and the *people* in it — this is the same idea for the *vehicles*
// key that rides along in the very same messages, kept separate so a scene can take the
// cars without taking anything else.
//
//   const detach = attachVehiclePresence(scene, 'town', {
//     onRoster(list),                 // every car parked here, on (re)join
//     onParked(car),                  // one car appeared
//     onRemoved(carId),               // one car left (driven off, garaged, sold, scrapped)
//     onPassengers(carId, ids),       // who is riding along right now
//     onPassengersSeed(map),          // {car_id: [ids]} for every crew already aboard,
//                                     //   on (re)join — the roster's answer to onPassengers
//     onError(code),                  // default: a toast with the Polish message
//     onBikeRoster(list),             // every bike parked on the street, on (re)join (town only)
//     onBikeParked(bike),             // one bike appeared or moved
//     onBikeRemoved(bikeId),          // one bike left (ridden off, parked elsewhere, sold, owner offline)
//   });
//
// Deliberately does NOT call realtime.subscribe/unsubscribe: the room membership belongs
// to attachRemotePresence, and unsubscribing here would kick the scene out of the room.
export function attachVehiclePresence(scene, room, handlers = {}) {
  const offs = [];
  const onError = handlers.onError
    ?? ((code) => showToast(scene, messageFor({ detail: { error_code: code } })));

  offs.push(realtime.on('roster', (m) => {
    if (m.room !== room) return;
    // The driving school's Yaris („school:<player id>") is not a vehicle in the registry —
    // it belongs to nobody, never parks and can't be boarded. Its driver's own avatar draws
    // it. Filtering here keeps a stray/forged entry from ever becoming a parked car.
    handlers.onRoster?.((m.vehicles ?? []).filter((c) => !isSchoolCarId(c?.car_id)));
    // Riders broadcast their own `move` as a WALK (the server refuses to let anyone but
    // the driver claim a car), so a client that learned about passengers only from
    // `car_passengers` would draw an already-seated rider jogging beside the car until
    // somebody boarded or left again. The roster seeds the crews it walked in on.
    handlers.onPassengersSeed?.(Object.fromEntries(
      Object.entries(m.passengers ?? {}).filter(([carId]) => !isSchoolCarId(carId))));
    handlers.onBikeRoster?.(m.bikes ?? []);
  }));
  // Bikes parked on the street (backend app/realtime/street_bikes.py) — shown like cars.
  offs.push(realtime.on('street_bike_parked', (m) => {
    if (m.room === room && m.bike) handlers.onBikeParked?.(m.bike);
  }));
  offs.push(realtime.on('street_bike_removed', (m) => {
    if (m.room === room && m.bike_id) handlers.onBikeRemoved?.(m.bike_id);
  }));
  offs.push(realtime.on('vehicle_parked', (m) => {
    if (m.room === room && m.car && !isSchoolCarId(m.car.car_id)) handlers.onParked?.(m.car);
  }));
  offs.push(realtime.on('vehicle_removed', (m) => {
    if (m.room === room && !isSchoolCarId(m.car_id)) handlers.onRemoved?.(m.car_id);
  }));
  offs.push(realtime.on('car_passengers', (m) => {
    if (m.room === room && !isSchoolCarId(m.car_id)) handlers.onPassengers?.(m.car_id, m.ids ?? []);
  }));
  // `car_error` is a private answer to something WE asked for (a refused car_board), so
  // it carries no room — it only ever reaches the socket that asked.
  offs.push(realtime.on('car_error', (m) => onError(m.error_code)));

  let detached = false;
  const detach = () => {
    if (detached) return;
    detached = true;
    offs.forEach(off => off());
    scene.events.off('shutdown', detach);
  };
  scene.events.once('shutdown', detach);
  return detach;
}

const TAG_TOUCH_MS = 2000;   // no hover on a touchscreen: a tapped tag times itself out

// A car standing on the street as everyone else sees it: the side silhouette in its lane,
// pointing the way it was parked. „Auto: Kasia" is a HOVER tag, not a permanent one — a
// street with four parked cars was four black labels floating over the road, and whose car
// it is only matters at the moment you point at it. Same gesture as the car controller's
// „Twoje auto" (CarController._tip): hover on a mouse, ~2 s on a tap.
// Returns the Container (with `.carId`); destroy it to remove the car.
export function renderParkedCar(scene, car, { depth, y } = {}) {
  const facing = car.facing === 'left' ? 'left' : 'right';
  // Its WHEELS go on the lane's line, and the body is drawn at that line's perspective size
  // (the near lane is closer to the viewer than the far one) — createCarSprite works both
  // out from the ground line. A caller that hands us a sprite-CENTRE y instead is answered
  // in kind: convert it back to the ground line it implies.
  const wheelY = y == null ? ROAD_WHEEL_Y[facing] : carGroundYFromCentre(car.model, y);
  const container = createCarSprite(scene, car.model, 'side', car.x, 0,
    { plate: car.plate, wheelY });
  setCarFacing(container, facing);
  setCarDepth(container, depth ?? wheelY);

  // Just above the roof — cars differ in height now, so measure the body we just drew.
  const bodyH = container.img?.displayHeight ?? 150;
  const tag = scene.add.text(0, -(bodyH / 2 + 35), `Auto: ${car.owner_name ?? ''}`, {
    fontFamily: 'system-ui', fontSize: '16px', color: '#fff',
    backgroundColor: '#000000aa', padding: { x: 6, y: 2 },
  }).setOrigin(0.5).setVisible(false);
  container.add(tag);          // inside the container so it moves and dies with the car

  // A Container has no texture to derive a hit area from, so give it the body's box.
  fitCarHitArea(container);
  let tagTimer = null;
  const showTag = (pointer) => {
    tag.setVisible(true);
    tagTimer?.remove();
    tagTimer = null;
    if (pointer?.wasTouch) {
      tagTimer = scene.time.delayedCall(TAG_TOUCH_MS, () => { tagTimer = null; tag.setVisible(false); });
    }
  };
  container.on('pointerover', showTag);
  container.on('pointerdown', (p) => { if (p?.wasTouch) showTag(p); });
  container.on('pointerout', () => { if (!tagTimer) tag.setVisible(false); });
  container.once('destroy', () => tagTimer?.remove());

  container.carId = car.car_id;
  container.ownerId = car.owner_id;
  container.nameTag = tag;
  return container;
}
